import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Layout } from '../components/Layout';
import { api } from '../api';
import type { CostOverview, NodeType } from '../types';
import { formatPrice, formatDateTime } from '../types';

export const Costs: React.FC = () => {
  const [costOverview, setCostOverview] = useState<CostOverview | null>(null);
  const [nodeTypes, setNodeTypes] = useState<NodeType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const [costs, types] = await Promise.all([
          api.getCosts(),
          api.getNodeTypes(),
        ]);

        if (!costs) {
          setError('Failed to load cost data');
        } else {
          setCostOverview(costs);
        }

        setNodeTypes(types);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Parse ISO 8601 duration format (e.g., "PT9M55.975415920S")
  const parseDuration = (duration: string): number => {
    // Parse PT format: PT[hours]H[minutes]M[seconds]S
    const regex = /PT(?:(\d+)H)?(?:(\d+)M)?(?:([\d.]+)S)?/;
    const match = duration.match(regex);

    if (!match) return 0;

    const hours = parseInt(match[1] || '0', 10);
    const minutes = parseInt(match[2] || '0', 10);
    const seconds = parseFloat(match[3] || '0');

    return (hours * 3600 + minutes * 60 + seconds) * 1000; // Convert to milliseconds
  };

  const formatDuration = (durationString: string): string => {
    const milliseconds = parseDuration(durationString);
    const totalMinutes = Math.floor(milliseconds / (1000 * 60));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const calculateTotalCost = (): number => {
    if (!costOverview) return 0;

    let total = 0;
    costOverview.usage.forEach(entry => {
      entry.usage.forEach(nodeUsage => {
        const nodeCost = costOverview.costs[nodeUsage.first.id] || 0;
        total += nodeCost;
      });
    });

    return total;
  };

  if (loading) {
    return (
      <Layout>
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        </Container>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          <Alert severity="error">{error}</Alert>
        </Container>
      </Layout>
    );
  }

  if (!costOverview) {
    return (
      <Layout>
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          <Alert severity="info">No cost data available</Alert>
        </Container>
      </Layout>
    );
  }

  const totalCost = calculateTotalCost();
  const startDate = formatDateTime(costOverview.start);
  const endDate = formatDateTime(costOverview.end);

  return (
    <Layout>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Cost Overview
        </Typography>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Billing Period
          </Typography>
          <Typography variant="body1" color="text.secondary">
            From: {startDate}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            To: {endDate}
          </Typography>
          <Typography variant="h5" sx={{ mt: 2 }}>
            Total Cost: {formatPrice(totalCost)}
          </Typography>
        </Paper>

        {costOverview.usage.length === 0 ? (
          <Alert severity="info">No usage data for this period</Alert>
        ) : (
          costOverview.usage.map(entry => (
            <Paper key={entry.project.id} sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                {entry.project.name}
              </Typography>

              {entry.usage.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No node usage for this project
                </Typography>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Node ID</TableCell>
                        <TableCell>Role</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell>Deleted</TableCell>
                        <TableCell align="right">Usage Duration</TableCell>
                        <TableCell align="right">Cost</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {entry.usage.map(nodeUsage => {
                        const node = nodeUsage.first;
                        const durationString = nodeUsage.second;
                        const nodeType = nodeTypes.find(nt => nt.id === node.type);
                        const cost = costOverview.costs[node.id] || 0;

                        return (
                          <TableRow key={node.id}>
                            <TableCell>{node.id.substring(0, 8)}...</TableCell>
                            <TableCell>{node.role.replace('_', ' ')}</TableCell>
                            <TableCell>
                              {nodeType ? `${nodeType.name} (${nodeType.provider})` : node.type}
                            </TableCell>
                            <TableCell>{formatDateTime(node.createdAt)}</TableCell>
                            <TableCell>{node.deletedAt ? formatDateTime(node.deletedAt) : 'Active'}</TableCell>
                            <TableCell align="right">{formatDuration(durationString)}</TableCell>
                            <TableCell align="right">{formatPrice(cost)}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              <Box sx={{ mt: 2, textAlign: 'right' }}>
                <Typography variant="h6">
                  Project Total:{' '}
                  {formatPrice(
                    entry.usage.reduce((sum, nodeUsage) => {
                      return sum + (costOverview.costs[nodeUsage.first.id] || 0);
                    }, 0)
                  )}
                </Typography>
              </Box>
            </Paper>
          ))
        )}
      </Container>
    </Layout>
  );
};
